import { useQuery } from '@tanstack/react-query';

import type { Cohort, CohortDefinitionStatus } from '@verantos-dev/types-cohorts';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { avKeys } from './queryKeys';

export interface CohortProfileQueryData {
  latestStatus: CohortDefinitionStatus;
  latestCohortVersionId: string;
  createdByUserId: string;
  latestSubjectCount: number;
  latestDatasetReleaseId?: string;
  latestDatasetReleaseDisplayName?: string;
}

interface CohortProfileQueryParams {
  projectId: string;
  cohortId: string;
  cohortVersionId: string;
}

/**
 * Fetch cohort profile data (status, latest version, creator).
 *
 * Keyed on `cohortVersionId` so that when a new version is created (save/revert),
 * the old cache entry goes stale and a fresh fetch returns the new status automatically.
 *
 * Features:
 * - Conditional polling every 5s during PROCESSING (matches legacy RTK Query behavior)
 * - `refetchOnReconnect` fixes the stale-PROCESSING bug (legacy had no reconnect recovery)
 * - `refetchOnWindowFocus` gives fresh status when user returns to the tab
 */
export const useCohortProfileQuery = (
  params: CohortProfileQueryParams,
  options?: { enabled?: boolean; shouldPoll?: boolean }
) => {
  const { projectId, cohortId, cohortVersionId } = params;

  return useQuery<CohortProfileQueryData>({
    queryKey: avKeys.profile(cohortVersionId),
    queryFn: async (): Promise<CohortProfileQueryData> => {
      const response = await authFetch<ObjectResponse<Cohort>>(
        `/cohorts/${projectId}/cohort-profile/${cohortId}`
      );

      return {
        latestStatus: response.data.latestStatus,
        latestCohortVersionId: response.data.latestCohortVersionId,
        createdByUserId: response.data.createdBy.userId,
        latestSubjectCount: response.data.latestSubjectCount,
        latestDatasetReleaseId: response.data.latestDatasetReleaseId,
        latestDatasetReleaseDisplayName: response.data.latestDatasetReleaseDisplayName,
      };
    },
    enabled: options?.enabled !== false && !!projectId && !!cohortId && !!cohortVersionId,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    refetchInterval: options?.shouldPoll ? 5000 : false,
  });
};
